import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { TopBar } from "../components/Navigation";
import OverviewTab from "./admin/OverviewTab";
import UsersTab from "./admin/UsersTab";
import ReportsTab from "./admin/ReportsTab";
import AdvertisingTab from "./admin/AdvertisingTab";
import SettingsTab from "./admin/SettingsTab";
import ContentTab from "./admin/ContentTab";
import MarketplaceTab from "./admin/MarketplaceTab";
import ChallengesTab from "./admin/ChallengesTab";
import SubscriptionsTab from "./admin/SubscriptionsTab";
import NotificationsTab from "./admin/NotificationsTab";
import AnalyticsTab from "./admin/AnalyticsTab";
import BrandsTab from "./admin/BrandsTab";
import {
    Shield, LayoutDashboard, Users, Flag, Megaphone,
    Settings as SettingsIcon, FileText, Store, Trophy,
    Crown, Bell, BarChart3, BadgeCheck,
} from "lucide-react";

const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "users", label: "Users", icon: Users },
    { id: "reports", label: "Reports", icon: Flag, badgeKey: "open" },
    { id: "content", label: "Content", icon: FileText },
    { id: "marketplace", label: "Marketplace", icon: Store },
    { id: "advertising", label: "Advertising", icon: Megaphone },
    { id: "brands", label: "Brands", icon: BadgeCheck },
    { id: "challenges", label: "Challenges", icon: Trophy },
    { id: "subscriptions", label: "Subscriptions", icon: Crown },
    { id: "notifications", label: "Notifications", icon: Bell },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const initialTab = params.get("tab") || "overview";
    const [tab, setTab] = useState(TABS.find((t) => t.id === initialTab) ? initialTab : "overview");
    const [stats, setStats] = useState(null);

    // Hard guard — bounce non-admins (or users still loading without role) away
    // from the admin shell instead of rendering an empty 403 state.
    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") {
            navigate("/feed", { replace: true });
        }
    }, [loading, user, navigate]);

    const refreshStats = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/stats");
            setStats(data);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => {
        if (user?.role !== "admin") return;
        refreshStats();
    }, [user, refreshStats]);

    useEffect(() => {
        if (params.get("tab") !== tab) {
            const next = new URLSearchParams(params);
            next.set("tab", tab);
            setParams(next, { replace: true });
        }
        if (user?.role === "admin") refreshStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-alpine-mist">
                <div className="w-10 h-10 border-4 border-ember-orange border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    if (!user || user.role !== "admin") return null;

    return (
        <div>
            <TopBar title="Admin" />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="hidden lg:flex items-center gap-3 mb-6">
                    <Shield className="w-8 h-8 text-ember-orange" />
                    <div>
                        <h1 className="font-display font-black text-4xl text-midnight-pine tracking-tight">
                            Admin console
                        </h1>
                        <p className="text-slate-gray">
                            Moderation, users, ads, challenges, subscriptions, and platform configuration.
                        </p>
                    </div>
                </div>

                {/* Tab strip */}
                <div className="bg-snowcap border border-ridge-line rounded-lg p-1.5 mb-6 flex gap-1 overflow-x-auto scrollbar-hide">
                    {TABS.map((t) => {
                        const active = tab === t.id;
                        const badge = t.badgeKey === "open" ? stats?.reports?.open || 0 : 0;
                        return (
                            <button
                                key={t.id}
                                data-testid={`admin-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-bold whitespace-nowrap transition-all ${
                                    active
                                        ? "bg-midnight-pine text-white shadow-sm"
                                        : "text-slate-gray hover:bg-alpine-mist hover:text-granite"
                                }`}
                            >
                                <t.icon className="w-4 h-4" />
                                {t.label}
                                {badge > 0 && (
                                    <span
                                        data-testid={`admin-tab-${t.id}-badge`}
                                        className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-ember-orange text-white text-[10px] font-bold"
                                    >
                                        {badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div data-testid={`admin-tab-content-${tab}`}>
                    {tab === "overview" && <OverviewTab stats={stats} onJump={setTab} />}
                    {tab === "users" && <UsersTab currentAdminId={user?.user_id} />}
                    {tab === "reports" && <ReportsTab />}
                    {tab === "content" && <ContentTab />}
                    {tab === "marketplace" && <MarketplaceTab />}
                    {tab === "advertising" && <AdvertisingTab />}
                    {tab === "brands" && <BrandsTab />}
                    {tab === "challenges" && <ChallengesTab />}
                    {tab === "subscriptions" && <SubscriptionsTab />}
                    {tab === "notifications" && <NotificationsTab />}
                    {tab === "analytics" && <AnalyticsTab />}
                    {tab === "settings" && <SettingsTab />}
                </div>
            </div>
        </div>
    );
}
